import { useCallback, useEffect, useMemo, useState } from 'react';

import usePublisherIntegrationTest, {
  IntegrationDomain,
  IntegrationVersion
} from 'api/usePublisherIntegrationTest';

import {
  FetchHeaders,
  FormValues,
  IExecuteIntegrationTestRequestDto,
  IExecuteResultDto,
  IJSONSchema,
  SchemaType
} from 'components/IntegrationTestModal/integration-test-modal.types';

interface ITestResult {
  statusCode: number;
  url: string;
}

type OnTestResult = (result: ITestResult) => void;

interface IUseIntegrationTestModalParams {
  publisherId: string;
  domain: IntegrationDomain;
  version: IntegrationVersion;
  title: string;
  callbackUrl: string;
  onTestResult?: OnTestResult;
}

interface IUseIntegrationTestModalReturn {
  isOpen: boolean;
  openModal: () => void;
  onClose: () => void;
  isLoading: boolean;
  isDataReady: boolean;
  schema: IJSONSchema | undefined;
  fetchHeaders: FetchHeaders;
  defaultValues: FormValues | undefined;
  schemaNames: string[];
  selectedSchemaName: string | undefined;
  onSchemaChange: (schemaName: string) => void;
  onTest: (request: IExecuteIntegrationTestRequestDto) => Promise<IExecuteResultDto>;
  title: string;
  url: string;
}

const EMPTY_SCHEMA: IJSONSchema = {
  type: SchemaType.OBJECT,
  properties: {}
};

export const useIntegrationTestModal = ({
  publisherId,
  domain,
  version,
  title,
  callbackUrl,
  onTestResult
}: IUseIntegrationTestModalParams): IUseIntegrationTestModalReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSchemaName, setSelectedSchemaName] = useState<string | undefined>(undefined);

  const {
    getSchemaNames,
    getRequestBodySchema: getRequestSchema,
    fetchRequestHeaders,
    getExamplePayload,
    executeTest,
    isLoading,
    isDataReady
  } = usePublisherIntegrationTest({
    publisherId,
    domain,
    version,
    schemaName: selectedSchemaName,
    enabled: isOpen
  });

  const schemaNames = useMemo(
    () => getSchemaNames.data || [],
    [getSchemaNames.data]
  );

  useEffect(() => {
    if (schemaNames.length > 0 && !selectedSchemaName) {
      setSelectedSchemaName(schemaNames[0]);
    }
  }, [schemaNames, selectedSchemaName]);

  const openModal = useCallback((): void => {
    setIsOpen(true);
  }, []);

  const onClose = useCallback((): void => {
    setIsOpen(false);
    setSelectedSchemaName(undefined);
  }, []);

  const handleSchemaChange = useCallback((schemaName: string): void => {
    setSelectedSchemaName(schemaName);
  }, []);

  const onTest = useCallback(
    async (request: IExecuteIntegrationTestRequestDto): Promise<IExecuteResultDto> => {
      const result = await executeTest.mutateAsync({
        headers: request.headers,
        payload: request.payload
      });
      onTestResult?.({
        statusCode: result.statusCode,
        url: callbackUrl
      });
      return result;
    },
    [executeTest, onTestResult, callbackUrl]
  );

  const schema = useMemo(
    () => getRequestSchema.data || EMPTY_SCHEMA,
    [getRequestSchema.data]
  );

  const defaultValues = useMemo(
    () => getExamplePayload.data,
    [getExamplePayload.data]
  );

  return {
    isOpen,
    openModal,
    onClose,
    isLoading,
    isDataReady,
    schema,
    fetchHeaders: fetchRequestHeaders,
    defaultValues,
    schemaNames,
    selectedSchemaName,
    onSchemaChange: handleSchemaChange,
    onTest,
    title,
    url: callbackUrl
  };
};
